import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import type { Page } from '../lib/api';
import type { CommandView } from '../lib/types';
import { useResource } from '../lib/useResource';
import { useSession } from '../lib/session';
import { Card, Empty, Loading, Notice, Pill, StatusPill, exactly, when } from '../components/ui';

export function Commands() {
  const { can, user } = useSession();
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [comment, setComment] = useState<Record<string, string>>({});

  const commands = useResource(
    () => api.get<Page<CommandView>>('/api/v1/commands', { status: status || undefined, size: 50 }),
    [status],
  );

  async function decide(command: CommandView, decision: 'APPROVE' | 'REJECT') {
    setBusyId(command.id);
    setError(null);
    try {
      await api.post(`/api/v1/commands/${command.id}/decision`, {
        decision,
        comment: comment[command.id] ?? '',
      });
      commands.reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'The decision could not be recorded');
    } finally {
      setBusyId(null);
    }
  }

  const pending = commands.data?.content.filter((c) => c.status === 'PENDING_APPROVAL') ?? [];

  return (
    <>
      <div className="page-head">
        <h1>Commands</h1>
        <p>
          Remote actions on managed endpoints. Destructive actions need two administrators, and the
          person who requested one cannot approve it.
        </p>
      </div>

      {error && <Notice kind="error">{error}</Notice>}

      {can('TECH_ADMIN') && pending.length > 0 && (
        <Card title="Awaiting your decision">
          {pending.map((command) => {
            const isOwnRequest = command.requestedBy === user?.id;
            return (
              <div
                key={command.id}
                style={{ borderBottom: '1px solid var(--line)', padding: '12px 0' }}
              >
                <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <Pill tone="stop">{command.verb.replace(/_/g, ' ').toLowerCase()}</Pill>
                  <strong>{command.assetTag ?? command.deviceId}</strong>
                  <span style={{ color: 'var(--muted)', fontSize: 13 }}>
                    requested by {command.requestedByName ?? 'unknown'} {when(command.requestedAt)} ·{' '}
                    {command.approvalsGiven}/{command.approvalsRequired} approvals
                  </span>
                </div>

                <p style={{ margin: '8px 0', fontSize: 13.5 }}>
                  <em>{command.reason}</em>
                </p>

                {Object.keys(command.parameters ?? {}).length > 0 && (
                  <p style={{ margin: '0 0 8px', color: 'var(--muted)', fontSize: 12.5 }} className="mono">
                    {JSON.stringify(command.parameters)}
                  </p>
                )}

                {isOwnRequest ? (
                  <Notice kind="info">
                    You requested this action, so a different administrator has to approve it.
                  </Notice>
                ) : (
                  <>
                    <label className="field" style={{ maxWidth: 560 }}>
                      <span>Comment</span>
                      <input
                        type="text"
                        value={comment[command.id] ?? ''}
                        onChange={(event) =>
                          setComment((current) => ({ ...current, [command.id]: event.target.value }))
                        }
                      />
                    </label>
                    <div className="actions">
                      <button
                        type="button"
                        className="danger"
                        disabled={busyId === command.id}
                        onClick={() => decide(command, 'APPROVE')}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className="secondary"
                        disabled={busyId === command.id}
                        onClick={() => decide(command, 'REJECT')}
                      >
                        Reject
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </Card>
      )}

      <Card>
        <div className="filters">
          <label className="field">
            <span>Status</span>
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">Any</option>
              {['PENDING_APPROVAL', 'APPROVED', 'DISPATCHED', 'ACKNOWLEDGED', 'COMPLETED',
                'FAILED', 'REJECTED', 'EXPIRED', 'CANCELLED'].map((value) => (
                <option key={value} value={value}>{value.replace(/_/g, ' ').toLowerCase()}</option>
              ))}
            </select>
          </label>
        </div>

        {commands.loading && !commands.data && <Loading />}
        {commands.data?.content.length === 0 && <Empty>No commands match this filter.</Empty>}

        {commands.data && commands.data.content.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Action</th><th>Device</th><th>Status</th><th>Reason</th>
                  <th>Requested by</th><th>Requested</th><th>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {commands.data.content.map((command) => (
                  <tr key={command.id}>
                    <td>{command.verb.replace(/_/g, ' ').toLowerCase()}</td>
                    <td><Link to={`/devices/${command.deviceId}`}>{command.assetTag ?? 'device'}</Link></td>
                    <td>
                      <StatusPill value={command.status} />
                      {command.approvalsRequired > 0 && (
                        <div style={{ color: 'var(--faint)', fontSize: 12 }}>
                          {command.approvalsGiven}/{command.approvalsRequired} approvals
                        </div>
                      )}
                    </td>
                    <td style={{ maxWidth: '32ch', fontSize: 12.5 }}>{command.reason}</td>
                    <td>{command.requestedByName ?? '—'}</td>
                    <td title={exactly(command.requestedAt)}>{when(command.requestedAt)}</td>
                    <td style={{ fontSize: 12.5, maxWidth: '30ch' }}>
                      {command.failureReason
                        ?? (command.completedAt ? `completed ${when(command.completedAt)}` : '—')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
